// /account-unlock?token=... — confirms an unlock token emailed when
// the per-account lockout fired (10 failed login attempts; see
// services/auth/login.ts).
//
// On success the lockout clears and failed_login_count resets to 0;
// the customer can sign in normally on the next attempt.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import * as authApi from '../api/auth.js';
import { extractApiError } from '../api/client.js';

export function AccountUnlock(): JSX.Element {
  const { t } = useTranslation(['auth', 'common']);
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<
    { kind: 'verifying' } | { kind: 'success' } | { kind: 'error'; message: string }
  >({ kind: 'verifying' });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: t('accountUnlock.error.missingToken') });
      return;
    }
    void (async () => {
      try {
        await authApi.confirmAccountUnlock(token);
        setState({ kind: 'success' });
      } catch (err) {
        setState({
          kind: 'error',
          message: extractApiError(err)?.message ?? t('accountUnlock.error.invalidOrExpired'),
        });
      }
    })();
  }, [token, t]);

  return (
    <div className="card">
      <h2>{t('accountUnlock.title')}</h2>
      {state.kind === 'verifying' && <p>{t('accountUnlock.verifying')}</p>}
      {state.kind === 'success' && (
        <>
          <div className="banner banner--success">
            <strong>{t('accountUnlock.success.banner')}</strong>
          </div>
          <p>
            <Trans
              i18nKey="accountUnlock.success.body"
              t={t}
              values={{ signIn: t('common:nav.signIn') }}
              components={[
                <Link key="signin" to="/login" />,
                <Link key="reset" to="/password-reset" />,
              ]}
            />
          </p>
        </>
      )}
      {state.kind === 'error' && (
        <>
          <div className="banner banner--error">
            <strong>{state.message}</strong>
          </div>
          <p>
            <Trans
              i18nKey="accountUnlock.error.body"
              t={t}
              components={[<Link key="reset" to="/password-reset" />]}
            />
          </p>
        </>
      )}
    </div>
  );
}
